import axios, { AxiosResponse } from 'axios'
import { cloneDeep } from 'lodash-es'
import { PropType, computed, defineComponent, onMounted, reactive, ref } from 'vue'

import { CsFilterBar, CsPrimaryButton, MsAdPlace, MsJsonLd, useHeaderStore } from '@sunrise/components'
import { MsBell } from '@sunrise/icons'
import { storeToRefs } from '@sunrise/pinia'
import { isMounted, screenSizeDetection, useGlobalStore } from '@sunrise/shared'
import { TrackingParameter, track } from '@sunrise/tracking'
import { Ads, LocationAutocomplete } from '@sunrise/types'

import { MailAgentTrackingVariant } from '../../../../components/MsMailAgentModal/MsMailAgentModal.enum'
import MsMailAgentModal from '../../../../components/MsMailAgentModal/MsMailAgentModal.vue'
import MsResultList from '../../../../components/MsResultList/MsResultList.vue'
import MsResultListFilter from '../../../../components/MsResultListFilter/MsResultListFilter.vue'
import MsResultListFilterModal from '../../../../components/MsResultListFilterModal/MsResultListFilterModal.vue'
import { ComponentName } from '../../../../components/MsResultListItem/MsResultListItem.enum'
import { SearchBoxDefault } from '../../../../components/MsSearchBox/MsSearchBox.enum'
import MsSearchBox from '../../../../components/MsSearchBox/MsSearchBox.vue'

import { decoratedItemsOf } from '../../../../ts/mapper/decoratedEstateMapper'
import { DecoratedEstate } from '../../../../ts/mapper/decoratedItemEstate.interface'
import { SortCriteria } from '../../../../ts/service/realEstateService.enum'
import { SearchFilters } from '../../../../ts/service/realEstateService.interface'
import { useResultListStore } from '../../../../ts/store/resultListStoreModule/resultListStoreModule'
import { ResultListStore } from '../../../../ts/store/resultListStoreModule/resultListStoreModule.interface'
import { scrollElementToTop } from '../../../../ts/utils/scrollUtils'
import { Direction } from '../../../../ts/utils/scrollUtils.enum'
import { Graph } from 'schema-dts'

import { tealiumTrackingParameter } from '../M_RealEstate_SearchList.enum'
import { ResultListData, SearchListState, SortCriteriaOptions } from '../M_RealEstate_SearchList.interface'
import { buildSearchBoxTracking, createMlmViewTracking, trackMlm } from '../M_RealEstate_SearchListTracking'

export default defineComponent({
    name: 'HydratedRealEstateSearchList',
    components: {
        MsResultListFilterModal,
        CsFilterBar,
        CsPrimaryButton,
        MsMailAgentModal,
        MsResultListFilter,
        MsSearchBox,
        MsResultList,
        MsAdPlace,
        MsJsonLd,
        MsBell,
    },
    props: {
        privacyPoliceUrl: String,
        resultListData: { type: Object as PropType<ResultListData>, required: true },
        items: { type: Object as PropType<DecoratedEstate[]>, required: true },
        searchFilters: { type: Object as PropType<SearchFilters>, required: true },
        jsonLd: { type: Object as PropType<Graph | undefined>, required: false },
        totalCount: { type: Number, required: true },
        locationLevels: { type: Array as PropType<LocationAutocomplete.LOCATION_LEVELS[]>, required: true },
    },
    setup(props) {
        const { isScreenM, isScreenXXL } = screenSizeDetection()
        const headerStore = useHeaderStore()
        const globalStore = useGlobalStore()
        const resultListStore = useResultListStore()
        const { searchParams } = storeToRefs(resultListStore)
        const isMountedRef = isMounted()
        const hasDot = computed(() => isMountedRef.value && !!headerStore.amount)
        const hasItems = computed(() => state.items.length !== 0)
        const floatingButtonIsCollapsed = ref(false)
        const pageUrl = globalStore.requestContext?.url.href || ''
        const servicePath = globalStore.urlPaths?.services
        const SERVICE_URL = `${servicePath}/get-items`
        const requestContext = useGlobalStore().requestContext
        const baseUrl = requestContext?.globalLinks.realEstateDetail
        const mlmViewTrackingId: Record<string, string> = createMlmViewTracking(
            ComponentName.RESULTLIST,
            pageUrl,
            props.items,
        )

        resultListStore.$subscribe(async (mutation, resultState) => {
            await fetchResults(resultState)
            const searchBoxTealiumData: TrackingParameter = buildSearchBoxTracking(
                tealiumTrackingParameter.AJAX_RELOAD,
                state.totalCount,
                props.totalCount,
                state.items,
            )
            track(cloneDeep(searchBoxTealiumData))
            trackMlm(ComponentName.RESULTLIST, pageUrl, state.items, resultState.searchParams.page as number)
            window.refreshAds?.(Ads.RefreshReason.USER_INTERACTION)
        })

        const isFilterModalVisible = ref<boolean>(false)
        const resultListComponent = ref<InstanceType<typeof MsResultList> | null>(null)
        const filterBarComponent = ref<InstanceType<typeof CsFilterBar> | null>(null)
        const elList = ref<HTMLDivElement | null>(null)
        const elFirstItem = ref<HTMLDivElement | null>(null)
        const state: SearchListState = reactive({
            floatingButtonIsVisible: false,
            modalIsVisible: false,
            items: props.items,
            searchFilters: props.searchFilters,
            isLoading: false,
            totalCount: props.totalCount,
        })
        let elResultListObserverBottomEdge: IntersectionObserver | null
        let elFirsrItemObserverBottomEdge: IntersectionObserver | null

        const sortCriteriaOptions: SortCriteriaOptions[] = [
            {
                label: 'Neueste Angebote',
                value: SortCriteria.CREATE_DATE_DESC,
            },
            {
                label: 'Höchster Preis',
                value: SortCriteria.PRICE_DESC,
            },
            {
                label: 'Entfernung',
                value: SortCriteria.DISTANCE,
            },
            {
                label: 'Niedrigster Preis',
                value: SortCriteria.PRICE,
            },
            {
                label: 'Kleinste Fläche',
                value: SortCriteria.LIVING_SPACE,
            },
            {
                label: 'Größte Fläche',
                value: SortCriteria.LIVING_SPACE_DESC,
            },
        ]

        function getAmountOfFilter(): number {
            const { eqid, ecat, epos, primi, prima, flmi, flma, roomi, rooma, furnid } = searchParams.value
            let counter = 0

            counter += (eqid?.length || 0) + (ecat?.length || 0) + (epos?.length || 0)
            counter += primi || prima ? 1 : 0
            counter += flmi || flma ? 1 : 0
            counter += roomi || rooma ? 1 : 0
            counter += furnid !== -1 ? 1 : 0

            return counter
        }

        function onUpdateSort(newSort: SortCriteria) {
            resultListStore.searchParams.sort = newSort
            resultListStore.resetPage()
        }

        function onClickBookmark() {
            headerStore.$patch({
                modalOpen: true,
            })
        }

        function getAmountOfResults(): string {
            if (state.totalCount === 0) {
                return '0 Ergebnisse'
            } else if (state.totalCount === 1) {
                return '1 Ergebnis'
            } else {
                return `${state.totalCount.toLocaleString('de-DE')} Ergebnisse`
            }
        }

        function observeResultListVisibility() {
            if (elList.value) {
                elResultListObserverBottomEdge = new IntersectionObserver(checkIntersectResultListBottom)
                elResultListObserverBottomEdge.observe(elList.value)
            }

            if (elFirstItem.value) {
                elFirsrItemObserverBottomEdge = new IntersectionObserver(checkIntersectFirstItemBottom)
                elFirsrItemObserverBottomEdge.observe(elFirstItem.value)
            }
        }

        const checkIntersectResultListBottom = (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                state.floatingButtonIsVisible = entry.boundingClientRect.bottom >= 0
            })
        }

        const checkIntersectFirstItemBottom = (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                floatingButtonIsCollapsed.value = entry.boundingClientRect.bottom < 0
            })
        }

        function shouldCollapseFloatingButton(): boolean {
            return floatingButtonIsCollapsed.value && !isScreenM.value
        }

        function openModal(): void {
            state.modalIsVisible = true
        }

        function closeModal(): void {
            state.modalIsVisible = false
        }

        function openFilterModal(): void {
            isFilterModalVisible.value = true
        }

        function closeFilterModal(): void {
            isFilterModalVisible.value = false
        }

        function closeFilterModalAndScroll(): void {
            isFilterModalVisible.value = false
            scrollFromBelow()
        }

        function scrollFromAbove() {
            scrollElementToTop(resultListComponent.value.$el, Direction.ABOVE, calculateOffset())
        }

        function scrollFromBelow() {
            scrollElementToTop(resultListComponent.value.$el, Direction.BELOW, calculateOffset())
        }

        function calculateOffset(): number {
            const filterBarMargin = isScreenM.value ? 32 : 0
            return filterBarComponent.value?.$el.offsetHeight + filterBarMargin
        }

        const fetchResults = async (resultState: ResultListStore): Promise<void> => {
            state.isLoading = true

            await axios
                .post(SERVICE_URL, resultState.searchParams)
                .then((response: AxiosResponse) => {
                    state.items = decoratedItemsOf(response.data.items, baseUrl)
                    state.searchFilters = response.data.searchFilters
                    state.totalCount = response.data.totalCount || SearchBoxDefault.TOTAL_COUNT
                })
                .catch((error) => {
                    console.error(error)
                    // todo error message?? We expected to show an message if an error occurs, but in pirobase we just show the same results talk to UI/UX/PO
                })
                .finally(() => {
                    state.isLoading = false
                })
        }

        onMounted(() => {
            elList.value = props.totalCount ? resultListComponent.value?.$refs.elList : null
            elFirstItem.value = props.totalCount ? resultListComponent.value?.$refs.elFirstItem[0] : null
            observeResultListVisibility()
            const searchBoxTealiumData: TrackingParameter = buildSearchBoxTracking(
                tealiumTrackingParameter.INITIAL,
                props.totalCount,
                props.totalCount,
                props.items,
            )
            track(cloneDeep(searchBoxTealiumData))
        })

        return {
            isScreenM,
            isScreenXXL,
            isFilterModalVisible,
            openFilterModal,
            closeFilterModal,
            closeFilterModalAndScroll,
            shouldCollapseFloatingButton,
            openModal,
            closeModal,
            scrollFromAbove,
            scrollFromBelow,
            onUpdateSort,
            onClickBookmark,
            getAmountOfResults,
            getAmountOfFilter,
            hasDot,
            hasItems,
            floatingButtonIsCollapsed,
            sortCriteriaOptions,
            searchParams,
            mlmViewTrackingId,
            resultListComponent,
            filterBarComponent,
            state,
            MailAgentTrackingVariant,
        }
    },
})
